import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, logout as authLogout } from '../utils/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faEnvelope, 
  faBuilding,
  faCrown,
  faEdit,
  faSave,
  faTimes,
  faSignOutAlt,
  faShieldAlt,
  faTasks
} from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import ChangePassword from '../components/ChangePassword';

const Profile = () => {
  const [user, setUser] = useState(getUser());
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [preferredCapacity, setPreferredCapacity] = useState(6);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
      });
      // Fetch preferred capacity
      fetchPreferredCapacity();
    }
  }, [user]);

  const fetchPreferredCapacity = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/burnout/preferences/capacity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferredCapacity(response.data.preferred_daily_capacity || 6);
    } catch (error) {
      console.error('Error fetching capacity:', error);
      // Set default if fetch fails
      setPreferredCapacity(6);
    }
  };

  const handleCapacityChange = async (newCapacity) => {
    setCapacityLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/burnout/preferences/capacity`,
        { preferred_capacity: newCapacity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreferredCapacity(newCapacity);
      toast.success(`Daily capacity updated to ${newCapacity} tasks`);
    } catch (error) {
      toast.error('Failed to update capacity');
      console.error('Error updating capacity:', error);
    } finally {
      setCapacityLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update local user state
      const updatedUser = { ...user, ...formData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profile updated successfully!');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: user.full_name || '',
      email: user.email || '',
    });
    setEditing(false);
  };

  const getUserTypeBadge = (type) => {
    const badges = {
      free: { text: 'Free', color: '#6b7280', icon: faUser },
      premium: { text: 'Premium', color: '#3b82f6', icon: faCrown },
      enterprise: { text: 'Enterprise', color: '#8b5cf6', icon: faBuilding },
    };
    return badges[type] || badges.free;
  };

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: '#FFB6C1' }}>Loading profile...</div>
      </div>
    );
  }

  const badge = getUserTypeBadge(user.user_type);

  return (
    <div style={{
      minHeight: '100vh',
      padding: '2rem'
    }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      {/* Navigation */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => navigate('/dashboard')} style={{
          background: 'none',
          border: '1px solid #FFB6C1',
          color: '#FFB6C1',
          padding: '0.5rem 1rem',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
          ← Back to Dashboard
        </button>
        <button onClick={authLogout} style={{
          background: 'none',
          border: '1px solid #999',
          color: '#999',
          padding: '0.5rem 1rem',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
          <FontAwesomeIcon icon={faSignOutAlt} /> Logout
        </button>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
          <FontAwesomeIcon icon={faUser} /> My Profile
        </h1>
        <p style={{ color: '#999', marginBottom: '2rem' }}>Manage your account information</p>

        {/* Profile Card */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', marginBottom: '2rem' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: '#FFB6C1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '2rem'
            }}>
              <FontAwesomeIcon icon={faUser} size="3x" color="#000" />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ marginBottom: '0.5rem' }}>{user.full_name}</h2>
              <p style={{ color: '#999', marginBottom: '0.5rem' }}>
                <FontAwesomeIcon icon={faEnvelope} /> {user.email}
              </p>
              <div style={{
                display: 'inline-block',
                backgroundColor: badge.color,
                color: '#fff',
                padding: '0.25rem 0.75rem',
                borderRadius: '4px',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}>
                <FontAwesomeIcon icon={badge.icon} /> {badge.text}
              </div>
            </div>
            {!editing && (
              <button onClick={() => setEditing(true)} style={{
                backgroundColor: '#0a0a0a',
                border: '1px solid #FFB6C1',
                color: '#FFB6C1',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}>
                <FontAwesomeIcon icon={faEdit} /> Edit
              </button>
            )}
          </div>

          {editing ? (
            <form onSubmit={handleSave}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#999', fontSize: '0.9rem' }}>
                  Full Name
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#999', fontSize: '0.9rem' }}>
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    color: '#666',
                    fontSize: '1rem'
                  }}
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>Email cannot be changed</small>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" disabled={loading} style={{
                  backgroundColor: '#FFB6C1',
                  border: 'none',
                  color: '#000',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}>
                  <FontAwesomeIcon icon={faSave} /> {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" onClick={handleCancel} disabled={loading} style={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #666',
                  color: '#666',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
              </div>
            </form>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '1px solid #333' }}>
                <span style={{ color: '#999' }}>Member Since:</span>
                <span>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', borderBottom: '1px solid #333' }}>
                <span style={{ color: '#999' }}>Account Type:</span>
                <span>{badge.text}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0' }}>
                <span style={{ color: '#999' }}>Username:</span>
                <span>{user.username || 'N/A'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Password Change Card */}
        <ChangePassword />

        {/* Burnout Watch Settings Card */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #FFB6C1',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{ color: '#FFB6C1', marginBottom: '0.5rem' }}>
            <FontAwesomeIcon icon={faShieldAlt} /> Burnout Watch Settings
          </h2>
          <p style={{ color: '#999', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Set your preferred daily task capacity for personalized workload guidance
          </p>

          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', color: '#fff', fontSize: '1rem', marginBottom: '0.25rem' }}>
                  <FontAwesomeIcon icon={faTasks} /> Daily Task Capacity
                </label>
                <span style={{ color: '#999', fontSize: '0.85rem' }}>
                  Maximum tasks you want to handle per day
                </span>
              </div>
              <div style={{
                backgroundColor: '#FFB6C1',
                color: '#000',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                {preferredCapacity}
              </div>
            </div>

            <input
              type="range"
              min="1"
              max="20"
              value={preferredCapacity}
              onChange={(e) => handleCapacityChange(parseInt(e.target.value))}
              disabled={capacityLoading}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: `linear-gradient(to right, #FFB6C1 0%, #FFB6C1 ${((preferredCapacity - 1) / 19) * 100}%, #333 ${((preferredCapacity - 1) / 19) * 100}%, #333 100%)`,
                outline: 'none',
                cursor: capacityLoading ? 'not-allowed' : 'pointer'
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
              <span>1 task</span>
              <span>20 tasks</span>
            </div>
          </div>

          <div style={{
            backgroundColor: '#0a0a0a',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '1rem',
            fontSize: '0.9rem'
          }}>
            <p style={{ color: '#999', marginBottom: '0.5rem' }}>
              💡 <strong style={{ color: '#FFB6C1' }}>How this works:</strong>
            </p>
            <ul style={{ color: '#999', paddingLeft: '1.5rem', margin: '0' }}>
              <li>Burnout Watch will warn you when you reach {Math.floor(preferredCapacity * 0.8)} tasks ({Math.floor((preferredCapacity * 0.8 / preferredCapacity) * 100)}% capacity)</li>
              <li>You'll get suggestions to reschedule when you hit {preferredCapacity} tasks (100% capacity)</li>
              <li>This is guidance only - you always have full control over your tasks</li>
            </ul>
          </div>
        </div>

        {/* Upgrade Card */}
        {user.user_type === 'free' && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #FFB6C1',
            borderRadius: '12px',
            padding: '2rem'
          }}>
            <h2 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
              <FontAwesomeIcon icon={faCrown} /> Upgrade to Premium
            </h2>
            <p style={{ color: '#999', marginBottom: '1.5rem' }}>
              Get access to advanced features and insights
            </p>
            <ul style={{ color: '#fff', marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Unlimited tasks per day</li>
              <li style={{ marginBottom: '0.5rem' }}>Advanced burnout analytics</li>
              <li style={{ marginBottom: '0.5rem' }}>Priority support</li>
              <li style={{ marginBottom: '0.5rem' }}>Custom resources</li>
              <li style={{ marginBottom: '0.5rem' }}>Team collaboration tools</li>
            </ul>
            <button style={{
              backgroundColor: '#FFB6C1',
              border: 'none',
              color: '#000',
              padding: '0.75rem 2rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem'
            }}>
              Upgrade Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
